import { cva, type VariantProps } from 'class-variance-authority'
import s from './Avatar.module.css'

/**
 * Pastille d'initiales — report de `.os-ava` du prototype validé (`mocks/ghp-bo/app.css`).
 *
 * Pas de primitive : un `<span>` suffit (corollaire de la règle 3). Rien n'est cliquable
 * ici — une pastille cliquable est un `Button` qui *contient* une pastille.
 *
 * ⚠️ **`aria-hidden`, et c'est un choix, pas un oubli.** Les initiales sont l'abréviation
 * d'un nom que le prototype imprime **toujours** juste à côté (`userCard`, `gestionComptes`,
 * `accDetail` : pastille + nom en clair). Sans cet attribut, un lecteur d'écran annoncerait
 * « JM Équipe Nord » — le nom deux fois, dont une en charabia. La pastille est donc
 * décorative au sens ARIA, et le contrat en tire la contrainte : **le nom doit être à côté,
 * en texte**. Un avatar seul, sans nom voisin, demanderait une prop de nom accessible — un
 * changement de contrat à demander, pas un attribut à retirer en silence.
 *
 * ⚠️ **Un seul fond.** Le prototype colore chaque pastille par une couleur propre à
 * l'équipe ou à l'agent (`background:${t.color}`) : cette donnée **n'existe pas dans notre
 * modèle** (ni `Team` ni `User` ne portent de couleur, cf. M6 / M22). On ne la fabrique pas
 * par hachage des initiales — ce serait une donnée inventée avec l'apparence d'une donnée
 * réelle, et deux équipes tomberaient sur la même teinte sans que personne ne comprenne
 * pourquoi. D'où le bleu de marque pour tout le monde, jusqu'à ce que le modèle tranche.
 *
 * **Une exception, tirée d'une donnée réelle** : le ton `danger` (`--red-600`), que `userCard`
 * pose sur un compte à **accès complet** — le rôle le porte, rien n'est inventé.
 */
const avatar = cva(s.root, {
  variants: {
    size: {
      /** 34 px — la rangée de rail de `gestionComptes`. */
      sm: s.sm,
      /** 40 px — la carte d'utilisateur (`userCard`) et l'en-tête de compte. */
      md: s.md,
      /** 52 px — la paire d'en-tête de la fiche équipe (`ficheEquipe`). */
      lg: s.lg,
      /** 64 px — le cadre photo de la fiche membre (`ficheMembre`). */
      xl: s.xl,
    },
    tone: {
      brand: null,
      /** `var(--red-600)` — l'accès complet de `userCard`. */
      danger: s.danger,
    },
  },
  defaultVariants: { size: 'md', tone: 'brand' },
})

export interface AvatarProps extends VariantProps<typeof avatar> {
  /**
   * Une ou deux lettres. Jamais un nom entier : la pastille fait 40 px de côté.
   *
   * La casse vient de l'appelant — le prototype ne déclare aucune transformation, et
   * majusculiser ici masquerait une donnée mal formée au lieu de la montrer.
   */
  initials: string
  className?: string
}

export function Avatar({ initials, size, tone, className }: AvatarProps) {
  return (
    <span className={avatar({ size, tone, className })} aria-hidden>
      {initials}
    </span>
  )
}

/**
 * La paire chevauchée du prototype : deux agents, **un** compte partagé (M6). C'est le seul
 * endroit du design system qui dise visuellement « ce compte n'a pas de titulaire ».
 *
 * Le chevauchement, le liseré blanc et l'atténuation de la seconde pastille vivent dans le
 * module CSS, appliqués par la position dans la paire (`+`) : c'est une affaire de voisinage,
 * pas un état — donc rien à publier sur le DOM, et aucune prop « second » à inventer.
 */
export interface TeamAvatarProps {
  initials: [string, string]
  size?: AvatarProps['size']
}

export function TeamAvatar({ initials, size }: TeamAvatarProps) {
  const [first, second] = initials

  return (
    <span className={s.pair}>
      <Avatar initials={first} size={size} />
      <Avatar initials={second} size={size} />
    </span>
  )
}
